/**
 * "Tidigare" i navbarens sökdialog: de senast besökta träffarna, sparade
 * lokalt i webbläsaren efter opt-in (se recent-search-toggle.tsx). Portad från
 * sajtens `recent-searches.ts` med samma nyckel, samma samtyckeskaka och samma
 * regler (dedup, en vecka, max fyra). Subdomänerna delar varken localStorage
 * eller kakan (sajtens kaka saknar domain), så listan och valet gäller bara
 * assistenten.
 *
 * Skillnad mot sajten: posten bär sin fulla `url`. Sajten länkar relativt
 * inom sig själv; härifrån leder varje träff till en annan origin.
 */

export const RECENT_SEARCHES_KEY = 'recent-searches'
// Äldre poster som behålls; med den nya blir listan högst fyra.
export const MAX_SEARCH_HISTORY = 3
// '1' = spara, '0' = spara inte, saknas = av. Samma namn (med stavfelet) som
// på sajten, så kontraktet är identiskt om de någon gång delas.
export const RECENT_SEARCH_CONSENT_COOKIE = 'resentSearchConsentV2'

export type RecentSearchItem = {
	id: string
	title: string
	canonicalPath: string
	type: string
	/** Absolut adress träffen ledde till. */
	url: string
}

export type RecentSearch = {
	item: RecentSearchItem
	savedAt: Date
}

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

/** Ny lista med `item` överst: dedup på canonicalPath, yngre än en vecka. */
export function withRecentSearch(
	existing: RecentSearch[],
	item: RecentSearchItem,
	now: Date = new Date(),
): RecentSearch[] {
	const oneWeekAgo = new Date(now.getTime() - ONE_WEEK_MS)
	return [
		{ item, savedAt: now },
		...existing
			.filter(
				r =>
					r.item.canonicalPath !== item.canonicalPath &&
					new Date(r.savedAt) > oneWeekAgo,
			)
			.slice(0, MAX_SEARCH_HISTORY),
	]
}

export function getCookie(name: string): string | null {
	const nameEQ = encodeURIComponent(name) + '='
	for (const part of document.cookie.split(';')) {
		const c = part.trim()
		if (c.startsWith(nameEQ)) return decodeURIComponent(c.slice(nameEQ.length))
	}
	return null
}

export function hasRecentSearchConsent(): boolean {
	return getCookie(RECENT_SEARCH_CONSENT_COOKIE) === '1'
}

// Läses vid anrop: Safari i privat läge kastar på localStorage-åtkomst, och en
// post utan `url` (äldre format) kan inte länkas och sorteras bort. Bara
// http(s), samma regel som för träffarna (search-dialog-rows.ts).
export function readRecentSearches(): RecentSearch[] {
	try {
		const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
		const parsed: unknown = raw ? JSON.parse(raw) : []
		return Array.isArray(parsed)
			? (parsed as RecentSearch[]).filter(
					r =>
						typeof r?.item?.url === 'string' &&
						/^https?:\/\//i.test(r.item.url),
				)
			: []
	} catch {
		return []
	}
}

function writeRecentSearches(list: RecentSearch[]): void {
	try {
		localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(list))
	} catch {
		// Historiken är en bekvämlighet, aldrig ett fel.
	}
}

/** Sparar en besökt träff om användaren gett samtycke; returnerar listan. */
export function saveRecentSearch(item: RecentSearchItem): RecentSearch[] {
	const current = readRecentSearches()
	if (!hasRecentSearchConsent()) return current
	const next = withRecentSearch(current, item)
	writeRecentSearches(next)
	return next
}

export function removeRecentSearch(id: string): RecentSearch[] {
	const next = readRecentSearches().filter(r => r.item.id !== id)
	writeRecentSearches(next)
	return next
}
